import { deleteAllWelcomeMessages, getCourseMessages, setCourseMessages } from '@/api'
import { queryClient } from '@/App'
import { WelcomeMessage } from '@/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

export function useWelcomeMessages() {
  const get = (courseName: string) =>
    useQuery({
      queryKey: ['welcomeMessages', courseName],
      queryFn: ({ signal }) => getCourseMessages(courseName, signal),
      retry: 1,
    })

  const set = useMutation({
    mutationFn: ({ courseName, messages }: { courseName: string; messages: WelcomeMessage[] }) =>
      setCourseMessages(courseName, messages),
    onSuccess: (_, variables) => {
      toast.success('Updated Welcome Messages', {
        description: `The welcome messages for ${variables.courseName} have been updated successfully`,
      })
      queryClient.invalidateQueries({ queryKey: ['welcomeMessages', variables.courseName] })
    },
    onError: (error) => {
      toast.error('Failed to update welcome messages', {
        description: error instanceof Error ? error.message : 'Please try again',
      })
    },
  })

  const deleteAll = useMutation({
    mutationFn: ({ courseName }: { courseName: string }) => deleteAllWelcomeMessages(courseName),
    onSuccess: (_, variables) => {
      toast.success('Deleted All Welcome Messages', {
        description: `All welcome messages for ${variables.courseName} have been deleted successfully`,
      })
      queryClient.invalidateQueries({ queryKey: ['welcomeMessages', variables.courseName] })
    },
    onError: (error) => {
      toast.error('Failed to delete welcome messages', {
        description: error instanceof Error ? error.message : 'Please try again',
      })
    },
  })

  return {
    get,
    set,
    deleteAll,
  }
}
